import React, { useContext } from 'react'
import { Box, Grid, Tooltip } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { CurrentTenantContext } from 'contexts/CurrentUserContext'
import ReportElementSortButton from 'components/ReportElement/ReportElementSortButton'

export type KeyRatingsChartElementHeaderProps = {
  showTrendCol: boolean
  sort: 'average' | 'percentile' | 'sort_order'
  direction?: 'asc' | 'desc'
  onSort: (value: null | string | number, direction: 'asc' | 'desc') => void
  print?: boolean
}

const KeyRatingsChartElementHeader: React.FC<
  KeyRatingsChartElementHeaderProps
> = ({ showTrendCol, onSort, sort, direction, print = false }) => {
  const tenant = useContext(CurrentTenantContext)

  return (
    <Box
      sx={{
        borderBottom: '1px solid #E0E0E0',
        padding: '8px 30px',
        fontSize: '12px',
        position: 'relative',
        zIndex: '1',
      }}
    >
      <Grid
        container
        spacing={8}
        alignItems="center"
        style={{ marginTop: '0px' }}
      >
        <Grid
          item
          sm={print ? 3 : 12}
          md={3}
          style={{ paddingTop: '0px', marginTop: '0px' }}
        >
          Key Measure
        </Grid>
        <Grid
          item
          sm={print ? 2 : 12}
          md={2}
          style={{ paddingTop: '0px', marginTop: '0px' }}
        >
          <Tooltip title={tenant?.rating_scale} placement="top">
            <Box
              sx={{ textAlign: 'center' }}
              display="flex"
              alignItems="center"
              m="0 auto"
              justifyContent="center"
            >
              {!print && (
                <ReportElementSortButton
                  onSort={onSort}
                  sortValue="average"
                  currentSort={{ value: sort, direction: direction || 'asc' }}
                />
              )}
              <Box pr={1}>Average</Box>
              {!print && (
                <Box fontSize="1rem" lineHeight={1}>
                  <HelpOutlineIcon fontSize="inherit" />
                </Box>
              )}
            </Box>
          </Tooltip>
        </Grid>
        <Grid
          item
          sm={print ? (showTrendCol ? 4 : 7) : 12}
          md={showTrendCol ? 4 : 7}
          style={{ paddingTop: '0px', marginTop: '0px' }}
        >
          <Tooltip
            title="Relative to the overall dataset"
            placement="top-start"
          >
            <Box
              sx={{ textAlign: 'center' }}
              display="flex"
              alignItems="center"
              m="0 auto"
            >
              {!print && (
                <ReportElementSortButton
                  onSort={onSort}
                  sortValue="percentile"
                  currentSort={{ value: sort, direction: direction || 'asc' }}
                />
              )}
              <Box pr={1}>Percentile Rank</Box>
              {!print && (
                <Box fontSize="1rem" lineHeight={1}>
                  <HelpOutlineIcon fontSize="inherit" />
                </Box>
              )}
            </Box>
          </Tooltip>
        </Grid>
        {showTrendCol && (
          <Grid
            item
            sm={print ? 3 : 12}
            md={3}
            sx={{ textAlign: 'center', paddingTop: '0px' }}
            style={{ paddingTop: '0px', marginTop: '0px' }}
          >
            <Tooltip title="Percentile Rank Over Time" placement="top-start">
              <Box
                sx={{ textAlign: 'center' }}
                display="flex"
                alignItems="center"
                m="0 auto"
              >
                <Box pr={1}>Trend Data</Box>
                {!print && (
                  <Box fontSize="1rem" lineHeight={1}>
                    <HelpOutlineIcon fontSize="inherit" />
                  </Box>
                )}
              </Box>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default KeyRatingsChartElementHeader
